'use client';

import { useEffect, useRef } from 'react';
import { Slider, SLIDER_MODE } from '@pixum/pixum-slider';
import type { ExpressDesignerImage } from 'data-layer';
import { useImageStateStore, type ImageInformation } from 'stores/express-designer';
import { useSelectedImageSync } from '../../hooks/useSelectedImageSync';
import { convertToImageInformation, getActiveImageId } from '../../utils';
import { UploadedImage } from './UploadedImage';

interface UploadedImageSliderProps {
  images: ExpressDesignerImage[];
  selectedImageId?: string;
  constants: Record<string, string>;
  className?: string;
}

export function UploadedImageSlider({ images, selectedImageId, constants, className }: UploadedImageSliderProps) {
  const storeImages = useImageStateStore(state => state.images);
  const storeSelectedImage = useImageStateStore(state => state.selectedImage);
  const setSelectedImage = useImageStateStore(state => state.setSelectedImage);
  const uploadBatchCompletedAt = useImageStateStore(state => state.uploadBatchCompletedAt);

  const allImages: ImageInformation[] = [
    ...storeImages,
    ...images.filter(img => !storeImages.some(storeImage => storeImage.uuid === img.id)).map(convertToImageInformation)
  ];

  const activeSelectedId = getActiveImageId(allImages, storeSelectedImage?.uuid ?? selectedImageId);

  const imageRefs = useRef<Map<string, HTMLElement>>(new Map());
  const storeImagesRef = useRef(storeImages);
  storeImagesRef.current = storeImages;

  const allImagesRef = useRef(allImages);
  allImagesRef.current = allImages;

  const storeSelectedImageRef = useRef(storeSelectedImage);
  storeSelectedImageRef.current = storeSelectedImage;

  /*
   * Sync the selected image object into the store whenever the active selection changes.
   * Runs on mount and whenever activeSelectedId changes (e.g. after login loads server images).
   */
  useEffect(() => {
    if (storeSelectedImageRef.current?.uuid === activeSelectedId || !activeSelectedId) return;

    const image = allImagesRef.current.find(img => img.uuid === activeSelectedId);

    if (image) {
      setSelectedImage(image);
    }
  }, [activeSelectedId, setSelectedImage]);

  useEffect(() => {
    if (!uploadBatchCompletedAt) return;

    const newest = storeImagesRef.current[0];

    if (newest) {
      setSelectedImage(newest);
    }
  }, [uploadBatchCompletedAt, setSelectedImage]);

  useSelectedImageSync({ activeSelectedId, selectedImageId, imageRefs });

  const handleSelect = (imageId: string) => {
    const image = allImages.find(img => img.uuid === imageId);

    if (image) {
      setSelectedImage(image);
    }
  };

  if (allImages.length === 0) return null;

  return (
    <Slider
      className={className}
      mode={SLIDER_MODE.AUTO}
      horizontalWheel
      aria-label={constants['i18n_choose_from_your_photos']}
    >
      {allImages.map(image => (
        <div
          key={image.uuid}
          ref={el => {
            if (el) imageRefs.current.set(image.uuid, el);
            else imageRefs.current.delete(image.uuid);
          }}
        >
          <UploadedImage image={image} isSelected={image.uuid === activeSelectedId} onSelect={handleSelect} />
        </div>
      ))}
    </Slider>
  );
}
